import React, { useEffect, useRef, useState } from 'react';
import { App, Checkbox, Modal, Spin, Typography } from 'antd';
import { useAuth } from '@clerk/clerk-react';

import { Button } from '@/components/Button';
import {
  AdminService,
  type ResultsExportCandidate,
  type ResultsExportParts,
  type ResultsExportSelection,
} from '@/services/adminService';
import styles from './ResultsExportModal.module.scss';

const NOT_COMPLETED_NOTE = 'Candidate has not completed yet';
const POLL_INTERVAL_MS = 2000;

type PartKey = keyof ResultsExportParts;

const PARTS: { key: PartKey; label: string; hts: boolean }[] = [
  { key: 'competency', label: 'Competency Results', hts: false },
  { key: 'htsAnswers', label: 'HTS Answers', hts: true },
  { key: 'htsReadiness', label: 'HTS Readiness Assessment + Scores', hts: true },
];

/** Candidates eligible for this part who haven't finished it (no-HTS templates don't count for HTS parts). */
const unfinishedFor = (part: { hts: boolean }, candidates: ResultsExportCandidate[]) =>
  part.hts
    ? candidates.filter((c) => c.htsAttached && !c.htsCompleted)
    : candidates.filter((c) => !c.competencyCompleted);

const eligibleFor = (part: { hts: boolean }, candidates: ResultsExportCandidate[]) =>
  part.hts ? candidates.filter((c) => c.htsAttached) : candidates;

/** No eligible candidate has finished this part. */
const isPartUnavailable = (part: { hts: boolean }, candidates: ResultsExportCandidate[]) =>
  unfinishedFor(part, candidates).length === eligibleFor(part, candidates).length;

const defaultParts = (candidates: ResultsExportCandidate[]): ResultsExportParts => {
  const parts: ResultsExportParts = { competency: false, htsReadiness: false, htsAnswers: false };
  for (const part of PARTS) parts[part.key] = !isPartUnavailable(part, candidates);
  return parts;
};

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Some browsers start the download asynchronously — revoking immediately can cancel it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

interface ResultsExportModalProps {
  open: boolean;
  selection: ResultsExportSelection;
  onClose: () => void;
}

/** Shared export flow for the results list and detail pages: pick parts, queue, poll, save. */
export const ResultsExportModal: React.FC<ResultsExportModalProps> = ({ open, selection, onClose }) => {
  const { getToken } = useAuth();
  const { notification } = App.useApp();

  const [candidates, setCandidates] = useState<ResultsExportCandidate[] | null>(null);
  const [parts, setParts] = useState<ResultsExportParts>({ competency: false, htsReadiness: false, htsAnswers: false });
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // Bumped on open/close so a stale poll loop stops instead of downloading.
  const runRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const run = ++runRef.current;
    setCandidates(null);
    setExporting(false);
    setProgress(null);
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const result = await new AdminService(token).getResultsExportAvailability(selection);
        if (runRef.current !== run) return;
        setCandidates(result);
        // Everything anyone has finished starts checked.
        setParts(defaultParts(result));
      } catch (e) {
        console.error(e);
        if (runRef.current !== run) return;
        notification.error({ message: 'Failed to load export options' });
        onClose();
      }
    })();
    return () => {
      runRef.current = run + 1;
    };
    // selection is a new object each render at some call sites; only reload on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleExport = async () => {
    const run = runRef.current;
    setExporting(true);
    setProgress(null);
    // Clerk tokens are short-lived and a large export can outlast one.
    const freshService = async () => {
      const token = await getToken();
      if (!token) throw new Error('No auth token');
      return new AdminService(token);
    };
    try {
      const exportId = await (await freshService()).createResultsExport(selection, parts);

      for (;;) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (runRef.current !== run) return;
        const state = await (await freshService()).getResultsExport(exportId);
        if (runRef.current !== run) return;
        setProgress(state.progress);
        if (state.status === 'failed') throw new Error('Export failed');
        if (state.status === 'done') {
          const blob = await (await freshService()).downloadResultsExport(exportId);
          if (runRef.current !== run) return;
          saveBlob(blob, state.filename ?? 'results_export');
          onClose();
          return;
        }
      }
    } catch (e) {
      console.error(e);
      if (runRef.current !== run) return;
      notification.error({ message: 'Export failed, try again' });
      setExporting(false);
    }
  };

  const visibleParts = candidates ? PARTS.filter((part) => !part.hts || candidates.some((c) => c.htsAttached)) : [];
  const nothingSelected = !parts.competency && !parts.htsReadiness && !parts.htsAnswers;
  const isBatch = (candidates?.length ?? 0) > 1;

  return (
    <Modal
      open={open}
      title={isBatch ? `Export ${candidates!.length} results to PDF` : 'Export results to PDF'}
      onCancel={onClose}
      centered
      maskClosable={!exporting}
      footer={
        <div className={styles.footer}>
          <Button appearance="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            appearance="solid"
            loading={exporting}
            disabled={!candidates || nothingSelected}
            onClick={handleExport}
          >
            Export
          </Button>
        </div>
      }
    >
      {!candidates ? (
        <div className={styles.loading}>
          <Spin size="small" />
        </div>
      ) : (
        <div className={styles.parts}>
          {visibleParts.map((part) => {
            const unfinished = unfinishedFor(part, candidates);
            const eligible = eligibleFor(part, candidates);
            const disabled = isPartUnavailable(part, candidates);
            return (
              <div key={part.key}>
                <Checkbox
                  checked={parts[part.key]}
                  disabled={disabled || exporting}
                  onChange={(e) => setParts((prev) => ({ ...prev, [part.key]: e.target.checked }))}
                >
                  {part.label}
                </Checkbox>
                {unfinished.length > 0 && (
                  <Typography.Text type="secondary" className={styles.note}>
                    {isBatch
                      ? `${unfinished.length}/${eligible.length} candidates haven't completed this yet`
                      : NOT_COMPLETED_NOTE}
                  </Typography.Text>
                )}
              </div>
            );
          })}
          {exporting && (
            <Typography.Text type="secondary">
              {progress && progress.total > 0 ? `Exporting ${progress.done} of ${progress.total}…` : 'Exporting…'}
            </Typography.Text>
          )}
        </div>
      )}
    </Modal>
  );
};
